import React from 'react';
import type { TaskItem } from '../lib/api';

// ── The tasks given in a class (or with Give a task) ─────────
// Each row is one task: what it is, what to do, and when it is due (empty:
// the student's next class day). Every student ticked gets each task, sends
// it from My Tasks, and their mentor reviews it (server/routes/tasks.js).

export const emptyTask = (): TaskItem => ({ title: '', details: '', dueDate: '' });

// The rows worth sending (a row with neither title nor details is skipped)
export const tasksToSend = (rows: TaskItem[]) => rows
  .map(r => ({ title: r.title.trim(), details: (r.details || '').trim(), dueDate: r.dueDate || '' }))
  .filter(r => r.title || r.details);

const TaskRows: React.FC<{ rows: TaskItem[]; onChange: (rows: TaskItem[]) => void; minDate?: string; requireDue?: boolean }> = ({ rows, onChange, minDate, requireDue }) => {
  const set = (i: number, k: keyof TaskItem, v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="rounded-xl border-2 border-gray-200 p-3 space-y-2 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-purple-700 bg-purple-50 rounded-full px-2 py-0.5">Task {i + 1}</span>
            {rows.length > 1 && (
              <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="ml-auto text-xs font-semibold text-red-600 hover:underline">Remove</button>
            )}
          </div>
          <input value={r.title} onChange={e => set(i, 'title', e.target.value)} maxLength={120}
            placeholder="Title, e.g. SQL joins practice" className="input-base" aria-label={`Task ${i + 1} title`} />
          <textarea value={r.details || ''} onChange={e => set(i, 'details', e.target.value)} rows={2} maxLength={1500}
            placeholder="What to do, e.g. Join orders and customers, find the top 5 customers by sales" className="input-base resize-none" aria-label={`Task ${i + 1} details`} />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-500">Due</span>
            <input type="date" value={r.dueDate || ''} min={minDate} onChange={e => set(i, 'dueDate', e.target.value)} className="input-base max-w-[170px]" aria-label={`Task ${i + 1} due date`} />
            {!r.dueDate && <span className="text-[11px] text-gray-400">{requireDue ? 'Pick the due date' : "Empty: the student's next class day"}</span>}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, emptyTask()])}
        className="w-full py-2 rounded-xl text-xs font-bold border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50">
        ＋ Add another task
      </button>
    </div>
  );
};

export default TaskRows;
